import React from "react";
import { createRoot } from "react-dom/client";
import DropdownActionSheet from "./DropdownActionSheet";
import type {
  ActionSheetResult,
  InternalActionSheetButton,
  InternalActionSheetOptions,
  TriggerPosition
} from "./types";

class ClickPositionTracker {
  private lastPosition: TriggerPosition | null = null;
  private isListening = false;

  startTracking() {
    if (this.isListening) {
      return;
    }

    this.isListening = true;

    // Track mouse clicks
    document.addEventListener("click", this.handleClick, true);
    document.addEventListener("mousedown", this.handleClick, true);

    // Track touch events
    document.addEventListener("touchstart", this.handleTouch, true);
  }

  stopTracking() {
    this.isListening = false;
    document.removeEventListener("click", this.handleClick, true);
    document.removeEventListener("mousedown", this.handleClick, true);
    document.removeEventListener("touchstart", this.handleTouch, true);
  }

  private handleClick = (e: MouseEvent) => {
    this.lastPosition = { x: e.clientX, y: e.clientY };
  };

  private handleTouch = (e: TouchEvent) => {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      this.lastPosition = { x: touch.clientX, y: touch.clientY };
    }
  };

  getLastPosition(): TriggerPosition | null {
    return this.lastPosition;
  }

  reset() {
    this.lastPosition = null;
  }
}

const positionTracker = new ClickPositionTracker();

class ActionSheetController {
  private container: HTMLDivElement | null = null;
  private root: ReturnType<typeof createRoot> | null = null;
  private isLoading = false;
  private cleanupTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Start tracking click positions when controller is created
    positionTracker.startTracking();
  }

  /**
   * Cancels a pending post-dismiss cleanup. Opening a new sheet (e.g. drilling
   * from a menu into a sub-sheet) reuses the single root/container, so the
   * previous sheet's scheduled unmount must be cancelled or it would tear down
   * the sheet that just opened. This is what makes nested/context-nav sheets work.
   */
  private clearCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /** Schedules the root unmount after the exit animation, cancelling any prior one. */
  private scheduleCleanup(delay = 300): void {
    this.clearCleanupTimer();
    this.cleanupTimer = setTimeout(() => {
      this.cleanupTimer = null;
      this.cleanup();
    }, delay);
  }

  validateTriggerPosition(
    position?: TriggerPosition | null
  ): TriggerPosition | false {
    if (position && position.x > 0 && position.y > 0) {
      return position;
    }
    return false;
  }

  showLoading(triggerPosition?: TriggerPosition): void {
    // A new sheet is taking over the root; cancel any pending unmount.
    this.clearCleanupTimer();
    this.isLoading = true;

    if (!this.container) {
      this.container = document.createElement("div");
      this.container.id = "action-sheet-container";
      document.body.appendChild(this.container);
      this.root = createRoot(this.container);
    }

    const lastClick = positionTracker.getLastPosition();
    let position = this.validateTriggerPosition(triggerPosition);

    if (!position) {
      const validLastClick = this.validateTriggerPosition(lastClick);
      if (validLastClick) {
        position = validLastClick;
      }
    }

    if (!position) {
      position = {
        x: window.innerWidth / 2,
        y: window.innerHeight - 100
      };
    }

    if (this.root) {
      this.root.render(
        <DropdownActionSheet
          isOpen={true}
          loading={true}
          onDidDismiss={() => {}}
          options={{ buttons: [], backdropDismiss: false }}
          triggerPosition={position}
        />
      );
    }
  }

  hide(): void {
    this.isLoading = false;
    if (this.root) {
      this.root.render(
        <DropdownActionSheet
          isOpen={false}
          onDidDismiss={() => {}}
          options={{ buttons: [] }}
        />
      );
    }

    this.scheduleCleanup();
  }

  async showActions<
    const TOptions extends InternalActionSheetOptions<
      readonly InternalActionSheetButton[]
    >
  >(
    options: TOptions,
    triggerPosition?: TriggerPosition
  ): Promise<ActionSheetResult<TOptions>> {
    options.buttons = options.buttons.filter(
      (button) => button.enabled !== false
    );

    // Opening a sheet takes over the single root; cancel a prior sheet's pending
    // unmount so drilling from one sheet into another doesn't tear down the new one.
    this.clearCleanupTimer();

    // If loading sheet is open, close it first and wait for animation
    if (this.isLoading && this.root) {
      this.root.render(
        <DropdownActionSheet
          isOpen={false}
          loading={true}
          onDidDismiss={() => {}}
          options={{ buttons: [], backdropDismiss: false }}
        />
      );
      this.isLoading = false;
      // Wait for exit animation
      await new Promise((r) => setTimeout(r, 200));
      this.cleanup();
    }

    const validPosition = this.validateTriggerPosition(triggerPosition);
    return new Promise((resolve) => {
      if (!this.container) {
        this.container = document.createElement("div");
        this.container.id = "action-sheet-container";
        document.body.appendChild(this.container);
        this.root = createRoot(this.container);
      }

      const lastClick = positionTracker.getLastPosition();
      let position = validPosition;

      if (!position) {
        const validLastClick = this.validateTriggerPosition(lastClick);
        if (validLastClick) {
          position = validLastClick;
        }
      }

      if (
        !position &&
        document.activeElement &&
        document.activeElement !== document.body
      ) {
        const rect = document.activeElement.getBoundingClientRect();
        position = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
      }

      if (!position) {
        position = {
          x: window.innerWidth / 2,
          y: window.innerHeight - 100
        };
      }

      const handleDismiss = (buttonId: string) => {
        if (this.root) {
          this.root.render(
            <DropdownActionSheet
              isOpen={false}
              onDidDismiss={() => {}}
              options={options}
              triggerPosition={position}
            />
          );
        }

        resolve(buttonId as ActionSheetResult<TOptions>);

        this.scheduleCleanup();
      };

      if (this.root) {
        this.root.render(
          <DropdownActionSheet
            isOpen={true}
            onDidDismiss={handleDismiss}
            options={options}
            triggerPosition={position}
          />
        );
      }
    });
  }

  private cleanup() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }
  }
}

export default new ActionSheetController();
